import {makeAutoObservable, runInAction} from "mobx";
import {getStore, useStore} from "../index";
import axios from "axios";
import {md5OfFile, updateImagePreview} from "@/utils";
import moment from "moment";


/**
 * 팝업 검색 파라미터
 * @typedef {object} PopupSearchMap
 * @property {string} searchTitle - 검색 제목
 * @property {number} offset - 페이지 오프셋
 * @property {number} limit - 페이지당 개수
 */


/**
 * 팝업 위치 정보
 * @typedef {object} PopupLocation
 * @property {string} top - 상단 위치값
 * @property {string} left - 좌측 위치값
 * @property {string} width - 팝업 가로 크기
 * @property {string} height - 팝업 세로 크기
 */


/**
 * 팝업 등록/수정 데이터
 * @typedef {object} PopupSettingData
 * @property {string} title - 팝업 제목
 * @property {PopupLocation} location - 팝업 위치 정보
 * @property {string} startDate - 노출 시작일
 * @property {string} endDate - 노출 종료일
 * @property {string} url - 연결 URL
 * @property {string|null} isActive - 노출 상태
 */


/**
 * 팝업 이미지 정보
 * @typedef {object} PopupImageInfo
 * @property {string} [md5] - 파일 md5 값
 * @property {string} [name] - 파일명
 * @property {string} [preview] - 미리보기 URL
 */


/**
 * 메인페이지 노출될 정보성 팝업 관리 Store
 */
export class PopupManageStore {

    /**
     * 공통 검색 Store
     * @type {Object}
     */
    searchBaseStore;

    /**
     * 팝업 검색 파라미터
     * @type {PopupSearchMap}
     */
    popupSearchMap = {
        searchTitle: "",
        offset: 0,
        limit: 10,
    }

    /**
     * 팝업 리스트
     * @type {Array<Object>}
     */
    popupList = [];

    /**
     * 팝업 전체 개수
     * @type {number}
     */
    popupListCount = 0;

    /**
     * 팝업 등록/수정 데이터
     * @type {PopupSettingData}
     */
    popupSettingData = {
        title: "",
        location: {
            top: "",
            left: "",
            width: "",
            height: "",
        },
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        url: "",
        isActive: null,
    };

    /**
     * 업로드 이미지 파일 리스트
     * @type {Array<File|PopupImageInfo|string>}
     */
    imageFile = [];

    /**
     * 이미지 미리보기 데이터
     * @type {Object<string, string>}
     */
    imagePreviewMap = {};


    constructor(store) {
        this.store = store;
        this.searchBaseStore = store.searchBaseStore;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    /**
     * 팝업 리스트 조회
     * @param {string|undefined} params - Query String
     * @param {string} type - 조회 타입
     * @returns {Promise<void>}
     */
    async setFetchPopupList(params = undefined, type = "") {
        let finalParams = "";
        let popupType = "";
        if(params){
            const search = new URLSearchParams(params.startsWith("?") ? params.slice(1) : params);
            finalParams = `?${search.toString()}`;
        }
        if(type === "list"){
            popupType = "/list"
        }
        const response = await axios.get(`/api/popup${popupType}${finalParams}`);
        if(response){
            runInAction(() => {
                this.popupList = response.data.data.popups;
                this.popupListCount = response.data.data.count;
            });
        }
    }

    /**
     * 선택 팝업 삭제
     * @param {string} name - 삭제명
     * @returns {Promise<any>|undefined}
     */
    async setDeletePopup (name) {
        if(this.popupList && this.popupList.length > 0){
            if (!this.searchBaseStore.checkedMap["popupListRow"] || this.searchBaseStore.checkedMap["popupListRow"].length === 0) return;
            const selectedItems = this.searchBaseStore.checkedMap["popupListRow"].map(index => this.popupList[index]);
            const items = selectedItems.map(item => item._id);
            const payLoad = {
                popupIds: items,
            }
            return await axios.delete("/api/popup", {data: payLoad});
        }
    }

    /**
     * 팝업 리스트 상태 초기화
     */
    setInitPopup() {
        this.popupSearchMap = {
            searchTitle: "",
            offset: 0,
            limit: 10,
        }
        this.popupList = [];
        this.popupListCount = 0;
    }

    /**
     * 팝업 설정 데이터 변경
     * @param {string} type - 변경 타입
     * @param {*} value - 변경 값
     * @param {string|undefined} supType - location 하위 타입
     */
    setPopupSettingData(type, value, supType = undefined) {
        if(supType){
            this.popupSettingData[type][supType] = value;
        }else {
            this.popupSettingData[type] = value;
        }
    }

    /**
     * 팝업 이미지 삭제
     * @param {number} index - 삭제 인덱스
     * @returns {Promise<void>}
     */
    async setRemovePopupImageFile(index) {
        const next = [...this.imageFile];
        next.splice(index, 1);
        this.imageFile = next;
        this.imagePreviewMap = updateImagePreview(next);
    }

    /**
     * 팝업 이미지 파일 설정
     * @param {Array<File>} files - 업로드 파일 리스트
     * @returns {Promise<void>}
     */
    async setPopupImageFilesData(files = []) {
        this.imageFile = files;
        this.imagePreviewMap = updateImagePreview(files);
    }

    /**
     * 팝업 수정 데이터 조회
     * @param {string} id - 팝업 ID
     * @returns {Promise<void>}
     */
    async setModifyPopupData(id) {
        const response = await axios.get(`/api/popup/detail?popupId=${id}`);
        if(response){
            const popupData = response.data.data?.popup;
            if(popupData){
                this.setPopupSettingData("title", popupData.title);
            }
            if(popupData.updatedAt){
                const date = moment(popupData.updatedAt).tz("Asia/Seoul").format("YYYY-MM-DD");
                this.setPopupSettingData("date", date);
            }
            if(popupData.startDate){
                const date = moment(popupData.startDate).tz("Asia/Seoul").format("YYYY-MM-DD HH:mm");
                this.setPopupSettingData("startDate", date);
            }
            if(popupData.endDate){
                const date = moment(popupData.endDate).tz("Asia/Seoul").format("YYYY-MM-DD HH:mm");
                this.setPopupSettingData("endDate", date);
            }
            if(popupData.data.url){
                this.setPopupSettingData("url", popupData.data.url);
            }

            if(typeof popupData.active === "boolean"){
                if(popupData.active){
                    this.setPopupSettingData("isActive", "active")
                } else {
                    this.setPopupSettingData("isActive", "hidden")
                }
            } else if(typeof popupData.active === "string"){
                if(popupData.active === "true"){
                    this.setPopupSettingData("isActive", "active")
                } else {
                    this.setPopupSettingData("isActive", "hidden")
                }
            }
            if(popupData.data.location){
                if(popupData.data.location.top){
                    this.setPopupSettingData("location", popupData.data.location.top, "top");
                }
                if(popupData.data.location.left){
                    this.setPopupSettingData("location", popupData.data.location.left, "left");
                }
                if(popupData.data.location.width){
                    this.setPopupSettingData("location", popupData.data.location.width, "width");
                }
                if(popupData.data.location.height){
                    this.setPopupSettingData("location", popupData.data.location.height, "height");
                }
            }
            if(popupData.data.image){
                const imageInfo = typeof popupData.data.image === "string" ? JSON.parse(popupData.data.image) : popupData.data.image
                this.imageFile = [imageInfo];
                this.imagePreviewMap = updateImagePreview(this.imageFile);
            }
        }
    }

    /**
     * 팝업 등록/수정
     * @param {string|undefined} id - 팝업 ID
     * @returns {Promise<any>}
     */
    async setPopupRegister(id = undefined) {
        const formData = new FormData();
        let fileInfos = [];
        const existingInfos = [];
        const newFiles = [];
        for (const file of this.imageFile) {
            if(file){
                if(typeof file === "string"){
                    if(JSON.parse(file).md5){
                        existingInfos.push(file);
                    } else {
                        newFiles.push(file);
                    }
                } else {
                    if(file.md5){
                        existingInfos.push(file);
                    } else {
                        newFiles.push(file);
                    }
                }
            }
        }
        for (const newFile of newFiles) {
            formData.append("file", newFile);
        }
        if (newFiles.length > 0) {
            const md5List = await Promise.all(newFiles.map(nf => md5OfFile(nf)));
            const newInfos = md5List.map((hash, i) => ({
                md5: hash,
                name: newFiles[i].name,
            }));
            fileInfos = existingInfos.concat(newInfos);
        } else {
            fileInfos = existingInfos;
        }
        const imageInfo = typeof fileInfos[0] === "string" ? JSON.parse(fileInfos[0]) : fileInfos[0];
        const viewState = this.popupSettingData["isActive"] === "active"
        formData.append("image", JSON.stringify(imageInfo));
        formData.append("title", this.popupSettingData["title"]);
        formData.append("url", this.popupSettingData["url"]);
        formData.append("location", JSON.stringify(this.popupSettingData["location"]));
        formData.append("startDate", this.popupSettingData["startDate"]);
        formData.append("endDate", this.popupSettingData["endDate"]);
        formData.append("active", viewState);
        if(id){
            formData.append("popupId", id);
        }
        return await axios.post("/api/popup", formData);
    }

    /**
     * 팝업 등록 상태 초기화
     */
    setInitPopupRegister() {
        this.popupSettingData = {
            title: "",
            location: {
                top: "",
                left: "",
                width: "",
                height: "",
            },
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
            url: "",
            isActive: null,
        };
        this.imageFile = [];
        this.imagePreviewMap = {};
    }

    /**
     * 검색 파라미터 초기화
     * @param {Object} param - 초기화 데이터
     */
    clearParams(param) {
        this.searchBaseStore.searchParams = param;
    }
}

/**
 * PopupManageStore Hook
 * @returns {PopupManageStore}
 */
export const usePopupManageStore = () => {
    return useStore().popupManageStore;
}

/**
 * PopupManageStore Getter
 * @returns {PopupManageStore}
 */
export const getPopupManageStore = () => {
    return getStore().popupManageStore;
}
